/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {translate} from '@ion/core/src/locale/locale';
import {CleanSessionMap} from '@ion/query/src/hooks/useVehicleMqttConnectionStatus';

export const CLEAN_SESSION_IN_PROGRESS_TOOLTIP = 'vehicle-manager:cleanSession.inProgressTooltip';

/**
 * Determines whether DM reports an active clean session for any of the given
 * ESNs, so actions that conflict with a clean session can be blocked.
 *
 * ```typescript
 * import {isCleanSessionInProgress} from
 *   '@ion/applications/src/pages/vehicle/manager/CleanSessionUtils';
 * ...
 *
 * const disableCreate = isCleanSessionInProgress(cleanSessionMap, vehicle.esn);
 * ```
 *
 * @param   cleanSessionMap The ESN to clean session state map from DM.
 * @param   esn             A single ESN or the list of ESNs for a vehicle.
 * @returns `true` when any ESN has a clean session in progress, else `false`.
 */
export const isCleanSessionInProgress = (
  cleanSessionMap: CleanSessionMap = {},
  esn?: string | string[]
): boolean => (
  (Array.isArray(esn) ? esn : [esn])
    .filter((value): value is string => Boolean(value))
    .some((value) => cleanSessionMap?.[value] === true)
);

/**
 * Resolves the tooltip explaining why an action is unavailable, or an empty
 * string when no clean session is in progress.
 *
 * ```typescript
 * import {getCleanSessionTooltip} from
 *   '@ion/applications/src/pages/vehicle/manager/CleanSessionUtils';
 * ...
 *
 * const title = getCleanSessionTooltip(cleanSessionInProgress);
 * ```
 *
 * @param   inProgress Whether a clean session is in progress for the device.
 * @returns The translated tooltip when `inProgress` is `true`, else `''`.
 */
export const getCleanSessionTooltip = (inProgress: boolean): string => (
  inProgress ? translate(CLEAN_SESSION_IN_PROGRESS_TOOLTIP) : ''
);
